import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { http, formatMoney } from "@/lib/api";
import { useVenueMarket } from "@/hooks/useVenueMarket";
import { Button } from "@/components/ui/button";
import { DASHBOARD } from "@/constants/testIds";
import { Plus, TrendingUp, Ticket, PoundSterling, DollarSign, Calendar } from "lucide-react";
import PartnerDividendsCard from "@/components/PartnerDividendsCard";

const StatCard = ({ id, label, value, icon: Icon, sub }) => (
  <div className="rounded-xl bg-white border border-zinc-200 p-6" data-testid={DASHBOARD.statCard(id)}>
    <div className="flex items-center justify-between">
      <div className="text-xs text-zinc-500 uppercase tracking-widest font-medium">{label}</div>
      <div className="w-8 h-8 grid place-items-center rounded-md bg-zinc-100">
        <Icon className="w-4 h-4 text-zinc-700" aria-hidden="true" />
      </div>
    </div>
    <div className="mt-4 font-display font-black text-3xl tracking-tight">{value}</div>
    {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
  </div>
);

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [venue, setVenue] = useState(null);
  const [saving, setSaving] = useState(false);
  const { currency, market, symbol } = useVenueMarket();
  const money = (p) => formatMoney(p, currency);
  const RevenueIcon = currency === "USD" ? DollarSign : PoundSterling;
  const bookingFeeMajor = (market.bookingFeeTiers?.[0]?.fee ?? 1.95);
  const bookingFeeStr = `${symbol}${bookingFeeMajor.toFixed(2)}`;
  const upliftStr = `${symbol}2`;

  const load = async () => {
    try {
      const [s, v] = await Promise.all([
        http.get("/dashboard/stats"),
        http.get("/venues/me"),
      ]);
      setStats(s.data);
      setVenue(v.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleHideBookingFee = async (value) => {
    setSaving(true);
    try {
      const { data } = await http.post("/venues/me/hide-booking-fee", { value });
      setVenue((v) => ({ ...v, hide_booking_fee_default: data.value }));
      toast.success(
        data.value
          ? `Booking fee hidden — ticket prices bumped by ${upliftStr} on ${data.events_updated} event${data.events_updated === 1 ? "" : "s"}`
          : `Booking fee restored on ${data.events_updated} event${data.events_updated === 1 ? "" : "s"}`
      );
      // Refresh stats since prices effectively changed
      load();
    } catch (e) {
      toast.error("Could not update setting");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-xs text-zinc-500 uppercase tracking-widest font-medium">Dashboard</div>
          <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl tracking-tight">
            Welcome back{venue ? `, ${venue.name}` : ""}
          </h1>
        </div>
        <Link to="/dashboard/events/new">
          <Button className="rounded-full bg-zinc-900 hover:bg-zinc-800 h-10 px-5" data-testid={DASHBOARD.createEventBtn}>
            <Plus className="w-4 h-4 mr-2" /> New event
          </Button>
        </Link>
      </div>

      {venue && !venue.stripe_onboarded && (
        <div className="mt-8 rounded-xl border border-amber-300 bg-amber-50 p-5 text-amber-900 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-semibold">Connect Stripe to receive payouts</div>
            <div className="text-sm opacity-90">Ticket revenue is paid directly to your Stripe account. It takes 2 minutes.</div>
          </div>
          <Link to="/dashboard/settings">
            <Button variant="outline" className="rounded-full border-amber-400 text-amber-900 hover:bg-amber-100">Set up Stripe</Button>
          </Link>
        </div>
      )}

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <StatCard id="tickets_sold" label="Tickets sold" value={stats?.tickets_sold ?? "—"} icon={Ticket} />
        <StatCard id="revenue" label="Gross revenue" value={money(stats?.gross_revenue_pence ?? 0)} icon={RevenueIcon} sub="excludes booking fees" />
        <StatCard id="orders" label="Paid orders" value={stats?.orders_count ?? "—"} icon={TrendingUp} />
        <StatCard id="events" label="Events" value={`${stats?.published_events ?? 0} / ${stats?.total_events ?? 0}`} icon={Calendar} sub="published / total" />
      </div>

      {/* Partner Dividends live card */}
      <div className="mt-6">
        <PartnerDividendsCard />
      </div>

      {/* Global hide-booking-fee toggle */}
      <div className="mt-8 rounded-xl bg-white border-2 border-emerald-200 p-6" data-testid="overview-hide-booking-fee-card">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display font-bold text-lg">Hide booking fee at checkout</h2>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-mono uppercase tracking-widest">
                Recommended
              </span>
            </div>
            <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
              The booking fee is charged <strong>per ticket</strong> ({bookingFeeStr} each). Leaving this off means a buyer
              ordering 3 tickets sees <strong>3 × {bookingFeeStr}</strong> stacked at checkout. Turn this on to bump every
              ticket tier on <strong>all your events</strong> by {upliftStr} automatically — buyers see a single all-in price.
              New events default to this setting.
            </p>
            {venue?.hide_booking_fee_default ? (
              <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium">
                ✓ Active on every event · buyers see prices +{upliftStr} (all-in)
              </div>
            ) : (
              <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 max-w-2xl" data-testid="overview-hide-booking-fee-warning">
                <strong>Heads up —</strong> booking fees will appear as a separate <strong>per-ticket line item</strong> for buyers on every event. Most venues prefer to hide this.
              </div>
            )}
          </div>

          {/* Big, clearly visible toggle button */}
          <button
            type="button"
            role="switch"
            aria-checked={!!venue?.hide_booking_fee_default}
            disabled={saving || !venue}
            onClick={() => toggleHideBookingFee(!venue?.hide_booking_fee_default)}
            data-testid="overview-hide-booking-fee"
            className={`relative inline-flex h-8 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
              venue?.hide_booking_fee_default ? "bg-emerald-600" : "bg-zinc-300"
            }`}
          >
            <span
              aria-hidden="true"
              className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow-md ring-0 transition-transform ${
                venue?.hide_booking_fee_default ? "translate-x-6" : "translate-x-0"
              }`}
            />
          </button>
        </div>
      </div>

      <div className="mt-10">
        <h2 className="font-display font-bold text-xl mb-4 tracking-tight">Upcoming events</h2>
        <div className="rounded-xl bg-white border border-zinc-200 divide-y divide-zinc-200">
          {(stats?.upcoming_events || []).length === 0 && (
            <div className="p-8 text-center text-zinc-500">
              No upcoming published events.{" "}
              <Link to="/dashboard/events/new" className="text-indigo-600 font-medium">Create one →</Link>
            </div>
          )}
          {(stats?.upcoming_events || []).map((e) => (
            <div key={e.id} className="p-5 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-semibold">{e.title}</div>
                <div className="text-sm text-zinc-500">
                  {new Date(e.start_at).toLocaleString(currency === "USD" ? "en-US" : "en-GB", { dateStyle: "medium", timeStyle: "short" })}
                  {e.venue_location ? ` · ${e.venue_location}` : ""}
                </div>
              </div>
              <div className="flex gap-2">
                {venue && (
                  <a href={`/v/${venue.slug}/e/${e.id}`} target="_blank" rel="noreferrer">
                    <Button variant="outline" className="rounded-full">View public page</Button>
                  </a>
                )}
                <Link to={`/dashboard/events/${e.id}/edit`}>
                  <Button className="rounded-full bg-zinc-900 hover:bg-zinc-800">Manage</Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
